import moment from 'moment';
import api from '@/api/telcoWorkflow';

const statuses = ['Verification', 'Upgrade', 'Provisioning', 'Provisioning Failed', 'Provisioned', 'Order Delay', 'Cooling Off', 'Ready', 'On Hold', 'On Hold - Future Live Date', 'On Hold - PAF Validation', 'On Hold - Sales Follow-up-Leads', 'Missed Appointment', 'Transfer Stopped'];

const order_types = ['FTTC - Restart','FTTC - New Line','FTTC - Migrate','FTTC - Upgrade','FTTP - Migrate','FTTP - New Line','FTTP - Upgrade','G-Fast - New Line','Line only - New Line','Line only - Restart','Line only - Takeover','Line only - Transfer','MPF - Downgrade','MPF - New Line','MPF - Restart','MPF - Takeover','MPF - Transfer','SoGEA - New Line','FTTC - Transfer','FTTC - Double Migrate'].sort();

const getDefaultState = () => ({
  telcoProductsThread: {},
  telcoProducts: [],
  fetchTelcoProductsLoading: false,
  fetchTelcoProductsError: false,
  fetchTelcoProductsErrorResponse: null,

  telcoWorkflowConfig: {
    limit: 25,
    pagination_count: 0,
    page: 1,
    sort: '',
    order: '',
    status: [...statuses],
    broadband_supplier: [],
    order_type: [],
    automated: "",
  },
  statuses: [...statuses],
  broadband_suppliers: ['TALK_TALK', 'BTW'],
  order_types: [...order_types],

  updateProvisioningInfoLoading: false,
  updateProvisioningInfoError: false,
  updateProvisioningInfoErrorResponse: null,
});

const state = getDefaultState();

const mutations = {
  FETCH_TELCO_PRODUCTS_BEGIN(state, id) {
    state.fetchTelcoProductsLoading = true;
    let telcoProductsThread = {};
    telcoProductsThread[id] = true;
    state.telcoProductsThread = telcoProductsThread;
    state.fetchTelcoProductsError = false;
    state.fetchTelcoProductsErrorResponse = null;
  },
  FETCH_TELCO_PRODUCTS_SUCCESS(state, payload) {
    state.fetchTelcoProductsLoading = false;
    state.telcoProducts = payload.data;
    state.telcoWorkflowConfig.pagination_count = parseInt(payload.headers['pagination-count']);
  },
  FETCH_TELCO_PRODUCTS_ERROR(state, err) {
    state.fetchTelcoProductsLoading = false;
    state.fetchTelcoProductsError = true;
    state.fetchTelcoProductsErrorResponse = err;
  },

  UPDATE_TELCO_WORKFLOW_PAGINATION(state, payload) {
    state.telcoWorkflowConfig.limit = payload.itemsPerPage;
    state.telcoWorkflowConfig.page = payload.page;
    state.telcoWorkflowConfig.sort = payload.sortBy.length ? payload.sortBy[0] : '';
    state.telcoWorkflowConfig.order = payload.sortDesc.length ? payload.sortDesc[0] ? 'desc' : 'asc' : '';
  },

  UPDATE_TELCO_WORKFLOW_FILTERS(state, payload) {
    state.telcoWorkflowConfig.page = 1;
    state.telcoWorkflowConfig.status = payload.status.length ? payload.status : state.statuses;
    state.telcoWorkflowConfig.broadband_supplier = payload.broadband_supplier;
    state.telcoWorkflowConfig.order_type = payload.order_type;
    state.telcoWorkflowConfig.automated = payload.automated;
  },

  UPDATE_PROVISIONING_INFO_BEGIN(state) {
    state.updateProvisioningInfoLoading = true;
    state.updateProvisioningInfoError = false;
    state.updateProvisioningInfoErrorResponse = null;
  },
  UPDATE_PROVISIONING_INFO_SUCCESS(state) {
    state.updateProvisioningInfoLoading = false;
  },
  UPDATE_PROVISIONING_INFO_ERROR(state, err) {
    state.updateProvisioningInfoLoading = false;
    state.updateProvisioningInfoError = true;
    state.updateProvisioningInfoErrorResponse = err;
  },

  RESET_TELCO_WORKFLOW_STORE(state) {
    Object.assign(state, getDefaultState());
  },
};

const actions = {
  async fetchTelcoProducts({ state, commit, dispatch }) {
    const id = moment().valueOf();
    commit('FETCH_TELCO_PRODUCTS_BEGIN', id);
    try {
      const response = await api.fetchTelcoProducts(state.telcoWorkflowConfig);
      if (state.telcoProductsThread[id]) {
        commit('FETCH_TELCO_PRODUCTS_SUCCESS', response);
      }
    } catch(err) {
      commit('FETCH_TELCO_PRODUCTS_ERROR', err.response.data.message);
      dispatch('alerts/setAlert', { type: 'error', message: err.response.data.message }, { root: true });
    }
  },

  updateTelcoWorkflowPagination({ commit, dispatch }, payload) {
    commit('UPDATE_TELCO_WORKFLOW_PAGINATION', payload);
    dispatch('fetchTelcoProducts');
  },

  updateTelcoWorkflowFilters({ commit, dispatch }, payload) {
    commit('UPDATE_TELCO_WORKFLOW_FILTERS', payload);
    dispatch('fetchTelcoProducts');
  },

  async updateProvisioningInfo({ commit, dispatch }, {product_id, data}) {
    commit('UPDATE_PROVISIONING_INFO_BEGIN');
    try {
      await api.updateProvisioningInfo(product_id, data);
      commit('UPDATE_PROVISIONING_INFO_SUCCESS');
    } catch(err) {
      commit('UPDATE_PROVISIONING_INFO_ERROR', err.response.data.message);
      dispatch('alerts/setAlert', { type: 'error', message: err.response.data.message }, { root: true });
    }
  }
};

const getters = {
  getTelcoProducts: state => {
    return state.telcoProducts.map( product => {
      return {
        product_id: product.product_details.id,
        status: product.status,
        business_name: product.lead.business_name,
        line_number: product.product_details.line_number,
        order_type: product.product_details.order_type,
        automated: product.product_details.automated,
        two_stage: product.product_details.two_stage ? 'Yes': 'No',
        status_entry: product.status_entry,
        broadband_supplier: product.product_details.broadband_supplier,
        product_detail__installation_appt_date: product.product_details.installation_appt_date,
        product_detail__expected_live_date: product.product_details.expected_live_date,
        quick_note: product.quick_note
      }
    });
  },
  getFetchTelcoProductsLoading: state => state.fetchTelcoProductsLoading,
  getFetchTelcoProductsError: state => state.fetchTelcoProductsError,
  getFetchTelcoProductsErrorResponse: state => state.fetchTelcoProductsErrorResponse,

  getTelcoWorkflowConfig: state => state.telcoWorkflowConfig,
  getStatuses: state => state.statuses,
  getBroadbrandSuppliers: state => state.broadband_suppliers,
  getOrderTypes: state => state.order_types,

  getUpdateProvisioningInfoLoading: state => state.updateProvisioningInfoLoading,
  getUpdateProvisioningInfoError: state => state.updateProvisioningInfoError,
  getUpdateProvisioningInfoErrorResponse: state => state.updateProvisioningInfoErrorResponse
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};